import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, ArrowLeft, ClipboardList, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminQuizzes() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedQuiz, setExpandedQuiz] = useState(null);
  const [form, setForm] = useState({
    title: "", chapter_id: "", time_limit_minutes: 15, max_attempts: 3, pass_percentage: 60, is_active: true, questions: []
  });
  const [qForm, setQForm] = useState({ question_text: "", options: ["", "", "", ""], correct_option: 0, image_url: "" });
  const [editingQ, setEditingQ] = useState(null);

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchData = async () => {
    const [courseRes, chaptersRes, quizzesRes] = await Promise.all([
      api.get(`/courses/${courseId}`),
      api.get(`/courses/${courseId}/chapters`),
      api.get(`/quizzes/course/${courseId}`),
    ]);
    setCourse(courseRes.data);
    setChapters(chaptersRes.data);
    setQuizzes(quizzesRes.data);
  };

  const resetForm = () => {
    setForm({ title: "", chapter_id: "", time_limit_minutes: 15, max_attempts: 3, pass_percentage: 60, is_active: true, questions: [] });
    setQForm({ question_text: "", options: ["", "", "", ""], correct_option: 0, image_url: "" });
    setEditingQ(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleAddQuestion = () => {
    if (!qForm.question_text || qForm.options.some(o => !o)) return;
    if (editingQ !== null) {
      const updated = [...form.questions];
      updated[editingQ] = { ...qForm };
      setForm({ ...form, questions: updated });
      setEditingQ(null);
    } else {
      setForm({ ...form, questions: [...form.questions, { ...qForm }] });
    }
    setQForm({ question_text: "", options: ["", "", "", ""], correct_option: 0, image_url: "" });
  };

  const handleEditQuestion = (idx) => {
    setQForm(form.questions[idx]);
    setEditingQ(idx);
  };

  const handleDeleteQuestion = (idx) => {
    setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.chapter_id || !form.title || form.questions.length === 0) {
      alert("Please fill title, select chapter, and add at least 1 question.");
      return;
    }
    const payload = { ...form, course_id: courseId };
    if (editing) {
      await api.put(`/quizzes/${editing}`, payload);
    } else {
      await api.post("/quizzes", payload);
    }
    resetForm();
    fetchData();
  };

  const handleEdit = async (quizId) => {
    const { data } = await api.get(`/quizzes/${quizId}/admin`);
    setForm({
      title: data.title, chapter_id: data.chapter_id, time_limit_minutes: data.time_limit_minutes,
      max_attempts: data.max_attempts, pass_percentage: data.pass_percentage, is_active: data.is_active,
      questions: data.questions || [],
    });
    setEditing(quizId);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this quiz and all attempts?")) {
      await api.delete(`/quizzes/${id}`);
      fetchData();
    }
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none admin-input";

  return (
    <AdminLayout>
      <div data-testid="admin-quizzes-page">
        <button onClick={() => navigate("/admin/courses")} className="flex items-center gap-1 text-sm text-[#0284C7] font-bold mb-3">
          <ArrowLeft size={16} /> Back to Courses
        </button>
        <div className="mb-4">
          <h1 className="text-xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Quizzes & Tests</h1>
          <p className="text-xs text-slate-500">{course?.title}</p>
        </div>

        <button data-testid="add-quiz-btn" onClick={() => { resetForm(); setShowForm(true); }} className="w-full bg-[#0284C7] text-white font-bold py-3 text-sm flex items-center justify-center gap-2 mb-4 rounded-lg">
          <Plus size={16} /> Create New Quiz
        </button>

        {/* Quiz Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border rounded-lg p-4 mb-4 space-y-3" data-testid="quiz-form">
            <h3 className="text-sm font-bold admin-text">{editing ? "Edit Quiz" : "Create New Quiz"}</h3>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase">Title *</label>
              <input data-testid="quiz-title" className={inputClass} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g., Chapter 1 Test" />
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase">Chapter *</label>
              <select data-testid="quiz-chapter" className={inputClass} value={form.chapter_id} onChange={e => setForm({ ...form, chapter_id: e.target.value })} required>
                <option value="">Select Chapter</option>
                {chapters.map(ch => <option key={ch.id} value={ch.id}>{ch.title}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Time (min)</label>
                <input data-testid="quiz-time" type="number" className={inputClass} value={form.time_limit_minutes} onChange={e => setForm({ ...form, time_limit_minutes: parseInt(e.target.value) || 15 })} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Attempts</label>
                <input data-testid="quiz-attempts" type="number" className={inputClass} value={form.max_attempts} onChange={e => setForm({ ...form, max_attempts: parseInt(e.target.value) || 3 })} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase">Pass %</label>
                <input data-testid="quiz-pass" type="number" className={inputClass} value={form.pass_percentage} onChange={e => setForm({ ...form, pass_percentage: parseInt(e.target.value) || 60 })} />
              </div>
            </div>

            {/* Questions List */}
            <div className="border-t border-slate-200 pt-3 mt-3">
              <h4 className="text-xs font-bold admin-text mb-2">Questions ({form.questions.length})</h4>
              {form.questions.map((q, i) => (
                <div key={i} className="bg-slate-50 rounded p-3 mb-2 text-xs">
                  <div className="flex justify-between items-start">
                    <p className="font-medium admin-text flex-1">Q{i + 1}. {q.question_text}</p>
                    <div className="flex gap-1 ml-2">
                      <button type="button" onClick={() => handleEditQuestion(i)} className="text-[#0284C7] p-1"><Edit size={12} /></button>
                      <button type="button" onClick={() => handleDeleteQuestion(i)} className="text-red-500 p-1"><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    {q.options.map((opt, j) => (
                      <span key={j} className={`px-2 py-1 rounded text-[10px] ${j === q.correct_option ? "bg-green-100 text-green-700 font-bold" : "bg-white text-slate-500"}`}>
                        {String.fromCharCode(65 + j)}. {opt}
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              {/* Add Question Form */}
              <div className="bg-blue-50 rounded-lg p-3 mt-2 space-y-2">
                <p className="text-[10px] font-bold text-[#0284C7] uppercase">{editingQ !== null ? "Edit Question" : "Add Question"}</p>
                <input data-testid="q-text" className={inputClass} placeholder="Question text" value={qForm.question_text} onChange={e => setQForm({ ...qForm, question_text: e.target.value })} />
                {qForm.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <button type="button" onClick={() => setQForm({ ...qForm, correct_option: i })} className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${i === qForm.correct_option ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                      {i === qForm.correct_option ? <Check size={12} /> : String.fromCharCode(65 + i)}
                    </button>
                    <input className={inputClass} placeholder={`Option ${String.fromCharCode(65 + i)}`} value={opt} onChange={e => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm({ ...qForm, options: opts }); }} />
                  </div>
                ))}
                <button type="button" data-testid="add-question-btn" onClick={handleAddQuestion} disabled={!qForm.question_text || qForm.options.some(o => !o)} className="w-full bg-[#0284C7] text-white text-xs font-bold py-2 rounded disabled:opacity-50">
                  {editingQ !== null ? "Update Question" : "+ Add Question"}
                </button>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="submit" data-testid="save-quiz-btn" className="flex-1 bg-green-600 text-white font-bold py-2.5 text-sm rounded-lg">{editing ? "Update Quiz" : "Save Quiz"}</button>
              <button type="button" onClick={resetForm} className="px-4 py-2.5 text-sm font-bold text-slate-500 border border-slate-300 rounded-lg">Cancel</button>
            </div>
          </form>
        )}

        {/* Quizzes List */}
        {quizzes.length > 0 ? (
          <div className="space-y-3">
            {quizzes.map(q => {
              const ch = chapters.find(c => c.id === q.chapter_id);
              return (
                <div key={q.id} className="admin-card border rounded-lg p-4" data-testid={`quiz-${q.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${q.is_active ? "bg-green-500 text-white" : "bg-slate-400 text-white"}`}>{q.is_active ? "ACTIVE" : "INACTIVE"}</span>
                        <span className="text-[10px] text-slate-500">{q.question_count} questions</span>
                      </div>
                      <h3 className="text-sm font-bold admin-text">{q.title}</h3>
                      {ch && <p className="text-[10px] text-[#0284C7] mt-0.5">{ch.title}</p>}
                      <div className="flex gap-4 mt-1 text-[10px] admin-text-secondary">
                        <span>{q.time_limit_minutes} min</span>
                        <span>{q.max_attempts} attempts</span>
                        <span>Pass: {q.pass_percentage}%</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button data-testid={`edit-quiz-${q.id}`} onClick={() => handleEdit(q.id)} className="p-2 text-[#0284C7] bg-[#0284C7]/10 rounded"><Edit size={14} /></button>
                      <button data-testid={`delete-quiz-${q.id}`} onClick={() => handleDelete(q.id)} className="p-2 text-red-500 bg-red-500/10 rounded"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : !showForm && (
          <div className="admin-card border rounded-lg p-8 text-center">
            <ClipboardList size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm admin-text-secondary">No quizzes created yet.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
